const DEFAULT_MAX_HISTORY = 100;

/**
 * Options for instantiating the HistoryManager class
 */
type HistoryManagerOptions = {
  /**
   * Maximum allowed states in the history. If a new state is pushed
   * when the manager is at the limit, the oldest state is removed from
   * the history.
   */
  maxHistory?: number;
};

/**
 * This class creates an instance that can be used to manage history.
 * One can use the push method to store any history state and can call
 * undo/redo method to get a previous/next state in the history.
 * @template State The type of the state stored in the history
 */
export class HistoryManager<State = any> {
  private currentIndex: number;
  private maxHistory: number;
  private states: State[];

  constructor({ maxHistory = DEFAULT_MAX_HISTORY }: HistoryManagerOptions) {
    this.currentIndex = 0;
    this.maxHistory = maxHistory;
    this.states = [];
  }

  get currentState() {
    return this.states[this.currentIndex];
  }

  get isEmpty() {
    return this.states.length === 0;
  }

  get hasUndo() {
    return !this.isEmpty && this.currentIndex > 0;
  }

  get hasRedo() {
    return !this.isEmpty && this.currentIndex < this.states.length - 1;
  }

  /**
   * Pushes a state to the history
   * @param state the state to be pushed into the history
   */
  push(state: State) {
    // if the current position is not the end of the array, clear all the
    // items after the current position
    if (this.currentIndex < this.states.length - 1) this.states.length = this.currentIndex + 1;
    // add the new state
    this.states.push(state);
    // if the states' size exceeded the allowed limit, remove the oldest state
    if (this.states.length > this.maxHistory) this.states.shift();
    this.currentIndex = this.states.length - 1;
  }

  /**
   * Retrieves the state from the history after walking the provided steps.
   * @param steps number of steps to travel in the history
   * @returns the state after walking the steps in the history
   */
  go(steps: number) {
    this.currentIndex = Math.min(Math.max(this.currentIndex + steps, 0), this.states.length - 1);
    return this.currentState;
  }

  /**
   * Go back in history by 1 step
   * @returns The previous state
   */
  undo() {
    return this.go(-1);
  }

  /**
   * Go forward in history by 1 step
   * @returns The previous state
   */
  redo() {
    return this.go(1);
  }

  /**
   * Clears the history
   */
  clear() {
    this.states.length = 0;
    this.currentIndex = 0;
  }
}
